import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  MdCheck,
  MdChevronRight,
  MdOutlineContentCopy,
  MdOutlineHistory,
  MdKeyboardArrowDown,
} from "react-icons/md";

import HyperLink from "../../../../../../Components/HyperLink/HyperLink";
import Tooltip from "../../../../../../Components/Tooltip/Tooltip";
import ImagesModal from "../../../../../Shipper/Customer/MainPages/Orders/Components/OrderHistoryModal/ImagesModal/ImagesModal";
import ShipmentHistoryModal from "../../../../../../Components/ShipmentHistoryModal/ShipmentHistoryModal";
import { linkColor } from "../../../../../Shipper/Customer/MainPages/ShipPage/Components/LinkColor/linkColors";
import LineNotes from "../../../../../Shipper/Customer/MainPages/Orders/Components/DeliveryRoutes/OrderLineNotes";
import noImage from "../../../../../../assets/images/noImage.png";
import { TONES, parcelTone } from "./tones";
import { seesOrderPrices } from "../../../../../../helpers/storeAccess";
import { useTranslation } from "react-i18next";

export { parcelTone };

/**
 * What fulfilling did to a Bolt line, from what the order already holds:
 * its fulfilment status, and the line's saleable flag and quantity (saved by
 * Attention / Partially fulfilled). Null when there's nothing to say yet.
 */
export const lineFulfilment = (item, orderStatusName) => {
  const status = String(orderStatusName ?? "").toLowerCase();
  if (status.startsWith("cancel")) return null;
  if (status.startsWith("fulfilled")) {
    return { tone: "success", label: "Fulfilled" };
  }
  const ordered = Number(item?.item_quantity) || 0;
  const saleable = String(item?.is_saleable);
  if (saleable === "1" || saleable === "true") {
    return { tone: "success", label: "Fulfilled" };
  }
  const raw = item?.saleable_item_quantity;
  if (raw !== null && raw !== undefined && raw !== "") {
    const done = Math.max(0, Number(raw) || 0);
    if (ordered > 0 && done >= ordered) {
      return { tone: "success", label: "Fulfilled" };
    }
    if (done > 0) {
      return { tone: "warning", label: `Fulfilled ${done} of ${ordered}` };
    }
    return { tone: "danger", label: "Not fulfilled" };
  }
  if (saleable === "0" || saleable === "false") {
    return { tone: "danger", label: "Not fulfilled" };
  }
  return null;
};

// The product ↔ parcel highlight follows a mouse only: on a touchscreen a
// tap counts as hovering, and the other rows stayed faded until the next tap
// somewhere else (ticking an item greyed out the rest of the list).
const mouseOnly = (handler) => (event) => {
  if (event.pointerType === "mouse") handler();
};

// Tints a link colour for chip/highlight backgrounds without caring whether
// it's hsl()/rgb()/hex — color-mix works on any valid CSS colour.
const tint = (color, percent) =>
  `color-mix(in srgb, ${color} ${percent}%, transparent)`;

// Reuses the shared state palette so rows stay legible in both themes.
const ACCENTS = [
  { bar: "bg-state-info-dot", chip: "bg-state-info-bg text-state-info-text" },
  {
    bar: "bg-state-purple-dot",
    chip: "bg-state-purple-bg text-state-purple-text",
  },
  {
    bar: "bg-state-success-dot",
    chip: "bg-state-success-bg text-state-success-text",
  },
  {
    bar: "bg-state-warning-dot",
    chip: "bg-state-warning-bg text-state-warning-text",
  },
  {
    bar: "bg-state-danger-dot",
    chip: "bg-state-danger-bg text-state-danger-text",
  },
  { bar: "bg-state-sky-dot", chip: "bg-state-sky-bg text-state-sky-text" },
];

const lengthUnit = (unit) => (unit === "Metric" ? "cm" : "in");
const weightUnit = (unit) => (unit === "Metric" ? "kg" : "lb");

const dimensions = (l, w, h, unit) => {
  if (!l && !w && !h) return null;
  return `${l ?? 0} × ${w ?? 0} × ${h ?? 0} ${unit}`;
};

const mediaSrc = (product) => {
  const primary = product?.media?.find((media) => media.is_primary === "true");
  return primary?.file || product?.media?.[0]?.file || noImage;
};

const Detail = ({ label, value, danger = false }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-wide text-ui-text-subtle">
      {label}
    </div>
    <div
      className={`break-words text-[12px] sm:truncate lg:text-[11.5px] ${
        danger ? "text-state-danger-text" : "text-ui-text"
      }`}
    >
      {value ?? "—"}
    </div>
  </div>
);

// Copy buttons: what they copy beside the icon (two sat side by side).
const COPY_BUTTON =
  "flex min-h-[40px] shrink-0 items-center gap-1 rounded-md px-1.5 text-[11.5px] font-medium text-ui-text-subtle transition-colors hover:text-brand lg:min-h-0 lg:py-0.5 [@media(pointer:coarse)]:min-h-[40px]";

// The small icon buttons: a 40px tap target below lg, as they were from lg up.
const ICON_BUTTON =
  "flex h-10 w-10 shrink-0 items-center justify-center text-ui-text-subtle transition-colors hover:text-brand lg:block lg:h-auto lg:w-auto";

// `pick` (optional, for whoever can fulfil the order): { stateOf(item) →
// "picked" | "missing" | null, toggle(item), isBusy(item) (optional: the
// tick is being saved) } — each Bolt line gets a Picked tick. `onTrackParcel(id)` (optional): a parcel row opens that parcel's
// tracking; without it the history icon opens the parcel's history pop-up.
export default function OrderItems({ data, pick, onTrackParcel }) {
  const { t } = useTranslation("warehouse");
  const user = useSelector((state) => state.user);
  // Products' fulfilment fees need "View price".
  const showPrices = seesOrderPrices(user);

  const shipment = data?.shipment;
  const unit = shipment?.unit;
  const parcels = useMemo(
    () => shipment?.shipment_details || [],
    [shipment?.shipment_details],
  );
  // The carrier's own tracking number per parcel (same order as the parcels).
  const carrierParcels =
    shipment?.c_p_shipment_details || data?.c_p_shipment_details || [];

  // null until someone picks a tab: then the default below applies.
  const [tab, setTab] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [fullview, setFullview] = useState();
  const [historyModal, setHistoryModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);

  // 1-based parcel numbers sharing each link_key, e.g. { L3: [7, 8, 11, 12] } —
  // a product can pack into several parcels, so this is many-to-one.
  const parcelNumbersByLink = useMemo(() => {
    const map = {};
    parcels.forEach((parcel, index) => {
      if (!parcel?.link_key) return;
      (map[parcel.link_key] ||= []).push(index + 1);
    });
    return map;
  }, [parcels]);

  const { boltItems, syncedItems } = useMemo(() => {
    const bolt = [];
    const synced = [];
    (data?.order_details || []).forEach((item) => {
      if (item?.plugin_item_id) synced.push(item);
      else bolt.push(item);
    });
    return { boltItems: bolt, syncedItems: synced };
  }, [data?.order_details]);

  const fulfilledQty = (pallet) => {
    const history = pallet?.pallet_history
      ?.slice()
      ?.sort((a, b) => b.id - a.id)
      ?.find((entry) => entry?.properties?.order_id === data?.id);

    if (!history) return null;
    const total =
      (history?.properties?.previous_quantity || 0) -
      (history?.properties?.updated_quantity || 0);
    return total > 0 ? total : null;
  };

  const copy = (values, key) => {
    navigator.clipboard
      .writeText(values.filter(Boolean).join(",\n"))
      .then(() => {
        setCopied(key);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((error) => console.error("Failed to copy:", error));
  };

  const canSeeSynced = user?.role !== "warehouseUser" && syncedItems.length > 0;
  // Opens on Bolt, or on Synced when the order has no Bolt lines but has
  // synced ones (a marketplace order not packed yet showed an empty list).
  const defaultTab = boltItems.length === 0 && canSeeSynced ? "synced" : "bolt";
  const chosenTab = tab ?? defaultTab;
  const activeTab =
    chosenTab === "synced" && !canSeeSynced ? "bolt" : chosenTab;

  const tabs = [
    { value: "bolt", label: t("orderView.boltPacked", { number: boltItems.length }) },
    ...(canSeeSynced
      ? [
          {
            value: "synced",
            label: t("orderView.syncedOrdered", { number: syncedItems.length }),
          },
        ]
      : []),
  ];

  const totalVolume = parcels.reduce(
    (sum, parcel) => sum + (parseFloat(parcel?.volume) || 0),
    0,
  );

  const parcelStatuses = parcels
    .map((parcel) => parcel?.tracking_status)
    .filter(Boolean);
  const allSameStatus =
    parcelStatuses.length === parcels.length &&
    parcelStatuses.length > 0 &&
    parcelStatuses.every((status) => status === parcelStatuses[0]);

  const parcelSubtitle = [
    parcels.length ? `${parcels.length}` : null,
    shipment?.total_weight
      ? `${shipment.total_weight} ${weightUnit(unit)}`
      : null,
    allSameStatus
      ? t("orderView.allStatus", { status: parcelStatuses[0].toLowerCase() })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const renderProduct = ({
    item,
    index,
    product,
    title,
    subtitle,
    meta,
    weight,
    linkKey,
    withState = false,
  }) => {
    const accent = ACCENTS[index % ACCENTS.length];
    const color = linkKey ? linkColor(linkKey) : null;
    const rowKey = `${activeTab}-${item?.id ?? index}`;
    const state = withState
      ? lineFulfilment(item, data?.fullfilment_status?.name)
      : null;
    const isOpen = expanded === rowKey;
    const pallets = product?.product_pallets || [];
    const linkedParcels = linkKey ? parcelNumbersByLink[linkKey] || [] : [];
    const isActive = Boolean(linkKey) && hoveredLink === linkKey;
    const isFaded = Boolean(hoveredLink) && hoveredLink !== linkKey;
    // Picked tick (Bolt lines, for whoever can fulfil the order).
    const pickState = withState && pick ? pick.stateOf(item) : null;
    const picked = pickState === "picked";
    const saving = Boolean(withState && pick?.isBusy?.(item));
    // Weight goes into the chip line on phones, so the name has room.
    const phoneOnlyChips =
      !state && linkedParcels.length === 0 && pickState !== "missing";

    return (
      <div
        key={rowKey}
        className="border-b border-ui-border-subtle last:border-b-0"
      >
        <div
          onPointerEnter={mouseOnly(() => linkKey && setHoveredLink(linkKey))}
          onPointerLeave={mouseOnly(() => linkKey && setHoveredLink(null))}
          style={
            color
              ? {
                  opacity: isFaded ? 0.4 : 1,
                  backgroundColor: isActive ? tint(color, 8) : undefined,
                  transition: "opacity .12s, background-color .12s",
                }
              : undefined
          }
          className="hover:bg-ui-row-hover"
        >
          <div className="flex items-center gap-2 px-3 py-2 sm:gap-3">
            <span
              className={
                color ? "" : `h-9 w-[3px] shrink-0 rounded-full ${accent.bar}`
              }
              style={
                color
                  ? {
                      height: 36,
                      width: 3,
                      borderRadius: 999,
                      flexShrink: 0,
                      backgroundColor: color,
                    }
                  : undefined
              }
            />

            {withState && pick && (
              // A 44px tap target on phones and tablets.
              <button
                type="button"
                role="checkbox"
                aria-checked={picked}
                aria-busy={saving || undefined}
                aria-label={`Picked: ${title}`}
                title={
                  saving
                    ? "Saving…"
                    : picked
                      ? "Picked: tap to untick"
                      : "Tick when picked"
                }
                disabled={saving}
                onClick={() => pick.toggle(item)}
                className={`-mx-1.5 -my-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md lg:mx-0 lg:my-0 lg:h-7 lg:w-7 [@media(pointer:coarse)]:-mx-1.5 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11 ${
                  saving ? "cursor-wait opacity-50" : ""
                }`}
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-md border-2 transition-colors lg:h-5 lg:w-5 ${
                    picked
                      ? "border-state-success-dot bg-state-success-dot text-white"
                      : "border-ui-border-strong bg-ui-surface"
                  }`}
                >
                  {picked && <MdCheck size={16} aria-hidden="true" />}
                </span>
              </button>
            )}

            <img
              src={mediaSrc(product)}
              alt=""
              loading="lazy"
              onError={(event) => {
                event.target.src = noImage;
              }}
              onClick={() => setFullview(mediaSrc(product))}
              className="h-10 w-10 shrink-0 cursor-pointer rounded border border-ui-border object-cover lg:h-8 lg:w-8"
            />

            {/* Phones: the name and code wrap rather than being cut off. */}
            <div className="min-w-0 flex-1">
              <div className="break-words text-[12.5px] font-medium text-ui-text sm:truncate">
                {title}
              </div>
              {subtitle && (
                <div className="break-words font-mono text-[12px] text-ui-text-subtle sm:truncate lg:text-[10.5px]">
                  {subtitle}
                </div>
              )}
              {/* Fulfilled, partly or not (and, on phones, its parcel and
                weight): what to pick and pack at a glance. */}
              {(state ||
                linkedParcels.length > 0 ||
                weight ||
                pickState === "missing") && (
                <div
                  className={`mt-1 flex flex-wrap items-center gap-1 ${
                    phoneOnlyChips ? "sm:hidden" : ""
                  }`}
                >
                  {pickState === "missing" && (
                    <span className="rounded bg-state-danger-bg px-1.5 py-0.5 text-[11px] font-semibold text-state-danger-text lg:text-[10.5px]">
                      Missing
                    </span>
                  )}
                  {state && (
                    <span
                      className={`rounded px-1.5 py-0.5 text-[11px] font-semibold lg:text-[10.5px] ${TONES[state.tone]}`}
                    >
                      {state.label}
                    </span>
                  )}
                  {linkedParcels.length > 0 && (
                    <span
                      className="whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-semibold sm:hidden"
                      style={{
                        color,
                        backgroundColor: tint(color, 12),
                        border: `1px solid ${tint(color, 25)}`,
                      }}
                    >
                      ↓ {linkedParcels.map((n) => `P${n}`).join(" · ")}
                    </span>
                  )}
                  {weight && (
                    <span className="text-[12px] text-ui-text-subtle sm:hidden">
                      {weight}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="hidden w-[120px] shrink-0 text-right text-[11.5px] text-ui-text-muted sm:block">
              {meta || ""}
            </div>
            <div className="hidden w-14 shrink-0 text-right text-[12px] text-ui-text-muted sm:block lg:text-[11.5px]">
              {weight || ""}
            </div>
            <div className="w-8 shrink-0 text-right text-[12.5px] font-semibold text-ui-text">
              {item?.item_quantity != null ? `×${item.item_quantity}` : ""}
            </div>

            {linkedParcels.length > 0 && (
              <span
                className="hidden shrink-0 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold sm:inline-block"
                style={{
                  color,
                  backgroundColor: tint(color, 12),
                  border: `1px solid ${tint(color, isActive ? 55 : 25)}`,
                }}
              >
                ↓ {linkedParcels.map((n) => `P${n}`).join(" · ")}
              </span>
            )}

            <button
              type="button"
              onClick={() => setExpanded(isOpen ? null : rowKey)}
              aria-expanded={isOpen}
              title={t("orderView.productDetails")}
              className={`${ICON_BUTTON} -my-1 -mr-2 lg:my-0 lg:mr-0`}
            >
              <MdKeyboardArrowDown
                size={16}
                className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
          </div>
          {/* The shipper's product note and the fulfilment note, the row's
            full width (under the name they were a narrow column on
            phones). */}
          <LineNotes
            item={item}
            className="-mt-1 pb-2 pl-[23px] pr-3 sm:pl-[27px]"
          />
        </div>

        {isOpen && (
          <div className="border-t border-ui-border-subtle bg-ui-surface-sunken px-3 py-2.5">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {showPrices && (
                <Detail
                  label={t("orderView.fulfilmentCost")}
                  value={product?.fulfillment_cost}
                />
              )}
              <Detail
                label={t("orderView.saleableQty")}
                value={item?.saleable_item_quantity}
                danger={String(item?.is_saleable) !== "1"}
              />
              <Detail label={t("orderView.orderedQty")} value={item?.item_quantity} />
              <Detail label={t("orderView.code")} value={product?.code || item?.sku} />
              <Detail label={t("orderView.dimensions")} value={meta} />
              <Detail label={t("orderView.weight")} value={weight} />
            </div>

            {product?.description && (
              <p className="m-0 mt-2 text-[12px] leading-[1.5] text-ui-text-muted lg:text-[11.5px]">
                {product.description}
              </p>
            )}

            {pallets.length > 0 && (
              <div className="mt-2.5">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ui-text-subtle">
                  {t("orderView.pallets")}
                </div>
                <div className="overflow-hidden rounded-md border border-ui-border">
                  {pallets.map((pallet, palletIndex) => (
                    <div
                      key={pallet?.id ?? palletIndex}
                      className="flex items-center gap-3 border-b border-ui-border-subtle bg-ui-surface px-2.5 py-1.5 text-[12px] last:border-b-0 lg:text-[11.5px]"
                    >
                      <span className="min-w-0 flex-1 break-words text-ui-text sm:truncate">
                        {pallet?.pallet?.name ?? "—"}
                      </span>
                      <span className="min-w-0 flex-1 break-words text-ui-text-muted sm:truncate">
                        {pallet?.pallet?.location?.name ?? "—"}
                      </span>
                      <span className="min-w-0 flex-1 break-words text-ui-text-muted sm:truncate">
                        {pallet?.product_lot?.name ?? "—"}
                      </span>
                      <span className="shrink-0 text-ui-text">
                        {fulfilledQty(pallet) != null
                          ? t("orderView.fulfilledQty", { qty: fulfilledQty(pallet) })
                          : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col lg:min-h-0 lg:flex-1">
      <ImagesModal setOpen={setFullview} open={fullview} />
      <ShipmentHistoryModal open={historyModal} setOpen={setHistoryModal} />

      {/* Below lg each panel is as tall as its rows and the order view
          scrolls as one page; from lg up two equal panels that scroll. The
          min-h-0 / flex-1 / overflow that let them shrink apply from lg
          only: below it they hid the rows. */}
      <div className="grid gap-2 lg:min-h-0 lg:flex-1 lg:grid-rows-[1fr_1fr]">
        <section className="flex flex-col overflow-hidden rounded-lg border border-ui-border bg-ui-surface lg:min-h-0">
          <header className="flex shrink-0 [flex-wrap:wrap] items-center gap-3 gap-y-2 border-b border-ui-border px-3 py-2 lg:flex-nowrap">
            <h3 className="m-0 shrink-0 text-[13.5px] font-semibold text-ui-text">
              {t("orderView.products")}
            </h3>
            <div className="flex min-w-0 items-center gap-0.5 rounded-lg bg-ui-surface-sunken p-0.5">
              {tabs.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setTab(value)}
                  className={`min-h-[40px] shrink-0 rounded-md px-2.5 py-1 text-[12px] font-semibold transition-colors lg:min-h-0 lg:text-[11.5px] ${
                    activeTab === value
                      ? "bg-ui-inverted text-white"
                      : "text-ui-text-muted hover:text-ui-text"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </header>

          <div className="thin__scrollbar lg:min-h-0 lg:max-h-[420px] lg:flex-1 lg:overflow-y-auto">
            {activeTab === "bolt" &&
              (boltItems.length ? (
                boltItems.map((item, index) =>
                  renderProduct({
                    item,
                    index,
                    product: item?.products,
                    title: item?.products?.name ?? "-",
                    subtitle: item?.products?.code,
                    meta: dimensions(
                      item?.products?.length,
                      item?.products?.width,
                      item?.products?.height,
                      item?.products?.dimension_unit || lengthUnit(unit),
                    ),
                    weight: item?.products?.weight
                      ? `${item.products.weight} ${item?.products?.weight_unit || weightUnit(unit)}`
                      : null,
                    linkKey: item?.link_key,
                    withState: true,
                  }),
                )
              ) : (
                <p className="m-0 px-3 py-6 text-center text-[12px] text-ui-text-subtle">
                  {t("orderView.noProductsOnThisOrder")}
                </p>
              ))}

            {activeTab === "synced" &&
              syncedItems.map((item, index) =>
                renderProduct({
                  item,
                  index,
                  product: item?.products,
                  title: item?.product_name ?? "-",
                  subtitle: item?.sku,
                  meta: dimensions(
                    item?.length,
                    item?.width,
                    item?.height,
                    lengthUnit(unit),
                  ),
                  weight: item?.weight
                    ? `${item.weight} ${weightUnit(unit)}`
                    : null,
                }),
              )}
          </div>
        </section>

        <section className="flex flex-col overflow-hidden rounded-lg border border-ui-border bg-ui-surface lg:min-h-0">
          <header className="flex shrink-0 items-center gap-2 border-b border-ui-border px-3 py-2">
            <h3 className="m-0 shrink-0 text-[13.5px] font-semibold text-ui-text">
              {t("orderView.parcels")}
            </h3>
            {parcelSubtitle && (
              <span className="truncate font-mono text-[11px] text-ui-text-subtle">
                {parcelSubtitle}
              </span>
            )}

            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              {parcels.length > 8 && (
                <span className="hidden text-[11px] text-ui-text-subtle lg:block">
                  {t("orderView.beyond8RowsThisListScrolls")}
                </span>
              )}
              {parcels.length > 0 && (
                <Tooltip
                  title={copied === "pid" ? t("orderView.copied") : t("orderView.copyAllParcelIds")}
                >
                  <button
                    type="button"
                    onClick={() =>
                      copy(
                        parcels.map((parcel) => parcel?.parcel_id),
                        "pid",
                      )
                    }
                    aria-label={t("orderView.copyAllParcelIds")}
                    className={COPY_BUTTON}
                  >
                    <MdOutlineContentCopy size={14} aria-hidden="true" />
                    {copied === "pid" ? t("orderView.copied") : "IDs"}
                  </button>
                </Tooltip>
              )}
              {carrierParcels.length > 0 && (
                <Tooltip
                  title={
                    copied === "sid"
                      ? t("orderView.copied")
                      : t("orderView.copyAllShipmentTrackingIds")
                  }
                >
                  <button
                    type="button"
                    onClick={() =>
                      copy(
                        carrierParcels.map(
                          (item) => item?.shipment_tracking_id,
                        ),
                        "sid",
                      )
                    }
                    aria-label={t("orderView.copyAllShipmentTrackingIds")}
                    className={COPY_BUTTON}
                  >
                    <MdOutlineContentCopy size={14} aria-hidden="true" />
                    {copied === "sid" ? t("orderView.copied") : "Carrier IDs"}
                  </button>
                </Tooltip>
              )}
            </div>
          </header>

          <div className="thin__scrollbar lg:min-h-0 lg:max-h-[420px] lg:flex-1 lg:overflow-y-auto">
            {parcels.length ? (
              parcels.map((parcel, index) => {
                const accent = ACCENTS[index % ACCENTS.length];
                const linkKey = parcel?.link_key;
                const color = linkKey ? linkColor(linkKey) : null;
                const isActive = Boolean(linkKey) && hoveredLink === linkKey;
                const isFaded = Boolean(hoveredLink) && hoveredLink !== linkKey;
                const dims = dimensions(
                  parcel?.length,
                  parcel?.width,
                  parcel?.height,
                  lengthUnit(unit),
                );
                // A tap anywhere on the row opens the parcel's tracking.
                const track =
                  onTrackParcel && parcel?.id != null
                    ? () => onTrackParcel(parcel.id)
                    : null;
                return (
                  <div
                    key={parcel?.id ?? index}
                    onPointerEnter={mouseOnly(
                      () => linkKey && setHoveredLink(linkKey),
                    )}
                    onPointerLeave={mouseOnly(
                      () => linkKey && setHoveredLink(null),
                    )}
                    {...(track
                      ? {
                          role: "button",
                          tabIndex: 0,
                          "aria-label": `Tracking for parcel P${index + 1}`,
                          onClick: track,
                          onKeyDown: (event) => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              track();
                            }
                          },
                        }
                      : {})}
                    style={
                      color
                        ? {
                            opacity: isFaded ? 0.4 : 1,
                            backgroundColor: isActive
                              ? tint(color, 8)
                              : undefined,
                            transition: "opacity .12s, background-color .12s",
                          }
                        : undefined
                    }
                    className={`border-b border-ui-border-subtle px-3 py-2 last:border-b-0 hover:bg-ui-row-hover ${
                      track ? "cursor-pointer" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={
                          color
                            ? ""
                            : `h-8 w-[3px] shrink-0 rounded-full ${accent.bar}`
                        }
                        style={
                          color
                            ? {
                                height: 32,
                                width: 3,
                                borderRadius: 999,
                                flexShrink: 0,
                                backgroundColor: color,
                              }
                            : undefined
                        }
                      />
                      <span
                        className={
                          color
                            ? "w-9 shrink-0 rounded px-1.5 py-0.5 text-center text-[10.5px] font-semibold"
                            : `w-9 shrink-0 rounded px-1.5 py-0.5 text-center text-[10.5px] font-semibold ${accent.chip}`
                        }
                        style={
                          color
                            ? {
                                color,
                                backgroundColor: tint(color, 15),
                              }
                            : undefined
                        }
                      >
                        P{index + 1}
                      </span>
                      <div className="hidden min-w-0 flex-1 truncate text-[12px] text-ui-text sm:block">
                        {parcel?.description ?? ""}
                      </div>
                      {/* The tracking link opens the public tracking page,
                          not the parcel's tracking here. */}
                      <div
                        className="min-w-0 flex-1 break-words text-[12px] sm:flex-none sm:basis-[210px] sm:truncate sm:text-[11.5px]"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {parcel?.parcel_id ? (
                          <HyperLink code={parcel.parcel_id} />
                        ) : (
                          <span className="text-ui-text-subtle">—</span>
                        )}
                      </div>
                      <div className="hidden w-[120px] shrink-0 text-right text-[11.5px] text-ui-text-muted sm:block">
                        {dims}
                      </div>
                      {/* Weight and status: on the line below on phones. */}
                      <div className="hidden w-14 shrink-0 text-right text-[12px] text-ui-text-muted sm:block lg:text-[11.5px]">
                        {parcel?.weight
                          ? `${parcel.weight} ${weightUnit(unit)}`
                          : ""}
                      </div>
                      <div className="hidden w-[72px] shrink-0 justify-end sm:flex">
                        {parcel?.tracking_status && (
                          <span
                            className={`truncate rounded px-1.5 py-0.5 text-[10.5px] font-medium ${TONES[parcelTone(parcel.tracking_status)]}`}
                          >
                            {parcel.tracking_status}
                          </span>
                        )}
                      </div>
                      {track ? (
                        <span
                          aria-hidden="true"
                          className={`${ICON_BUTTON} -my-2 -mr-2 lg:my-0 lg:mr-0`}
                        >
                          <MdChevronRight size={18} />
                        </span>
                      ) : (
                        <Tooltip title={t("orderView.clickToViewHistory")}>
                          <button
                            type="button"
                            onClick={() => setHistoryModal(parcel?.id)}
                            className={`${ICON_BUTTON} -my-2 -mr-2 lg:my-0 lg:mr-0`}
                          >
                            <MdOutlineHistory size={15} />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                    {(dims ||
                      parcel?.description ||
                      parcel?.weight ||
                      parcel?.tracking_status) && (
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 pl-[27px] text-[12px] text-ui-text-subtle sm:hidden">
                        <span className="min-w-0 break-words">
                          {[
                            parcel?.description,
                            dims,
                            parcel?.weight
                              ? `${parcel.weight} ${weightUnit(unit)}`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                        {parcel?.tracking_status && (
                          <span
                            className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${TONES[parcelTone(parcel.tracking_status)]}`}
                          >
                            {parcel.tracking_status}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="m-0 px-3 py-6 text-center text-[12px] text-ui-text-subtle">
                {t("orderView.noParcelsYet")}
              </p>
            )}
          </div>

          {parcels.length > 0 && (
            <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-ui-border px-3 py-2 text-[12px] text-ui-text-muted lg:text-[11.5px]">
              <span>
                {t("orderView.totalParcels", { count: parcels.length })}
              </span>
              <span>
                {[
                  shipment?.total_weight
                    ? `${shipment.total_weight} ${weightUnit(unit)}`
                    : null,
                  totalVolume ? `${totalVolume.toFixed(2)} cbm` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </footer>
          )}
        </section>
      </div>
    </div>
  );
}
